// Flattens the host contact record (+ the search-row summary) into the fields
// the card header/contact rows show. Experience/education render straight from
// the raw record via the shared ExperienceView/EducationView.
import type { HostContact } from '../../../api/hostContact';
import type { CandidateSummary } from '../../../types';

export interface ProfileView {
  name: string;
  headline?: string;
  avatarUrl?: string;
  phone?: string;
  email?: string;
  source?: string;
  linkedInUrl?: string;
  twitterUrl?: string;
  githubUrl?: string;
  externalHref: string;
  creditedTo?: string;
  creditedToAvatar?: string;
  skills: string[];
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;

const fullName = (c: HostContact): string =>
  [str(c.first_name), str(c.last_name)].filter(Boolean).join(' ');

const primaryEmail = (c: HostContact): string | undefined => {
  const list = Array.isArray(c.emails) ? c.emails : [];
  return str(list.find((e) => e?.primary)?.email) ?? str(list[0]?.email);
};

const phone = (c: HostContact): string | undefined => {
  const num = str(c.local_phone_number);
  if (!num) return undefined;
  const code = str(c.phone_code);
  return code ? `${code} ${num}` : num;
};

export const buildProfileView = (
  initial: CandidateSummary,
  contact?: HostContact | null
): ProfileView => {
  const c = contact ?? null;
  const skills = c && Array.isArray(c.skills) ? c.skills.filter(Boolean) : [];

  return {
    name: (c ? fullName(c) : '') || initial.name,
    headline: (c ? str(c.position) : undefined) ?? str(initial.headline),
    avatarUrl:
      initial.avatarUrl ??
      (c ? (str(c.image_url) ?? str(c.avatar)) : undefined),
    phone: c ? phone(c) : undefined,
    email: (c ? primaryEmail(c) : undefined) ?? str(initial.email),
    source: c ? str(c.ats_candidate_source?.name) : undefined,
    linkedInUrl: c ? str(c.social?.linkedin_url) : undefined,
    twitterUrl: c ? str(c.social?.twitter_url) : undefined,
    githubUrl: c ? str(c.social?.github_url) : undefined,
    externalHref: `/candidates/${initial.id}`,
    creditedTo: c ? str(c.credited_to?.name) : undefined,
    creditedToAvatar: c ? str(c.credited_to?.avatar) : undefined,
    skills: skills.length > 0 ? skills : initial.tags,
  };
};
